import { CalendarIcon } from "lucide-react";
import { useRef, useState } from "react";
import {
  DatePicker as AriaDatePicker,
  DatePickerProps as AriaDatePickerProps,
  Button,
  DateValue,
  ValidationResult,
} from "react-aria-components";
import { Calendar } from "./Calendar";
import { DateInput } from "./DateField";
import { Dialog } from "./Dialog";
import { Description, FieldError, FieldGroup, Label } from "./Field";
import { Popover } from "./Popover";
import { composeTailwindRenderProps } from "./utils";

export interface DatePickerProps<T extends DateValue>
  extends AriaDatePickerProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  placeholder?: string;
}

export function DatePicker<T extends DateValue>({
  label,
  description,
  errorMessage,
  ...props
}: DatePickerProps<T>) {
  const [focused, setFocused] = useState(false);

  const showPlaceholder = !focused && !props.value;
  const dateInputRef = useRef<HTMLInputElement>(null);

  return (
    <AriaDatePicker
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "group flex flex-col gap-1",
      )}
      isInvalid={props.isInvalid ?? (errorMessage ? true : undefined)}
      onFocus={() => {
        setFocused(true);
        dateInputRef.current?.focus();
      }}
      onBlur={() => setFocused(false)}
    >
      {label && <Label>{label}</Label>}
      <FieldGroup className="border-theme-outline-variant flex items-center justify-between gap-x-4 rounded-sm border p-7">
        {props.placeholder && showPlaceholder ? (
          <Button
            className="text-theme-on-background-variant w-full text-left text-sm font-medium"
            onPress={() => dateInputRef.current?.focus()}
          >
            {props.placeholder}
          </Button>
        ) : (
          <DateInput ref={dateInputRef} />
        )}
        <Button className="text-theme-on-background hover:bg-theme-on-background-hover relative z-1 -m-1 cursor-pointer rounded p-1 outline-offset-0">
          <CalendarIcon className="size-5" />
        </Button>
      </FieldGroup>
      {description && <Description>{description}</Description>}
      <FieldError className="mt-2 ml-[5px]">{errorMessage}</FieldError>
      <Popover
        className="colors-glass backdrop-blur-glass rounded-md"
        offset={8}
        placement="bottom"
      >
        <Dialog className="bg-transparent">
          <Calendar />
        </Dialog>
      </Popover>
    </AriaDatePicker>
  );
}
